import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import { Button } from "../../ui/button";
import { Plus, Lock, Eye } from "lucide-react";
import { HighlightEditor } from "./HighlightEditor";
import { FAQEditor } from "./FAQEditor";
import { SecondaryImagesEditor } from "./SecondaryImagesEditor";
import { SeoPagePreviewDialog } from "./SeoPagePreviewDialog";
import { SeoHeroImageUpload } from "./SeoHeroImageUpload";
import { SeoVideoUpload } from "./SeoVideoUpload";
import { useSeoPageForm } from "../../../hooks/useSeoPageForm";

export const SeoPageEditor = ({ page, slug, onSaved }) => {
  const {
    form, update, saving, uploadingHero, handleHeroUpload,
    uploadingVideo, handleVideoUpload, removeVideo,
    updateHighlight, addHighlight, removeHighlight, moveHighlight,
    updateFaq, addFaq, removeFaq, moveFaq,
    save, previewOpen, setPreviewOpen, openPreview,
  } = useSeoPageForm(page, slug, onSaved);

  return (
    <div className="space-y-8" data-testid="seo-page-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-muted-foreground bg-secondary/40 border rounded px-3 py-2 w-fit" data-testid="seo-locked-url">
          <Lock size={14} /> URL: <span className="font-mono">{slug}</span> (no editable)
        </div>
        <Button data-testid="seo-preview-button" type="button" variant="outline" onClick={openPreview}>
          <Eye size={16} className="mr-2" /> Vista previa
        </Button>
      </div>

      <SeoPagePreviewDialog open={previewOpen} onOpenChange={setPreviewOpen} slug={slug} />

      <div className="space-y-4">
        <h4 className="font-semibold text-sm">Contenido principal</h4>
        <div>
          <Label>Etiqueta</Label>
          <Input data-testid="seo-field-eyebrow" value={form.eyebrow} onChange={(e) => update("eyebrow", e.target.value)} />
        </div>
        <div>
          <Label>Título principal (H1)</Label>
          <Input data-testid="seo-field-h1" value={form.h1} onChange={(e) => update("h1", e.target.value)} />
        </div>
        <div>
          <Label>Texto introductorio</Label>
          <Textarea data-testid="seo-field-intro" rows={4} value={form.intro} onChange={(e) => update("intro", e.target.value)} />
        </div>
      </div>

      <SeoHeroImageUpload heroImage={form.hero_image} uploading={uploadingHero} onUpload={handleHeroUpload} />

      <div className="space-y-3">
        <h4 className="font-semibold text-sm">Fotografías secundarias</h4>
        <SecondaryImagesEditor images={form.secondary_images} onChange={(imgs) => update("secondary_images", imgs)} />
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm">Beneficios (lo que incluye este servicio)</h4>
          <Button data-testid="seo-add-highlight-button" variant="outline" size="sm" onClick={addHighlight}>
            <Plus size={14} className="mr-1" /> Agregar beneficio
          </Button>
        </div>
        <div>
          <Label>Subtítulo de la sección</Label>
          <Input data-testid="seo-field-highlights-title" value={form.highlights_title} onChange={(e) => update("highlights_title", e.target.value)} />
        </div>
        <div className="space-y-3">
          {form.highlights.map((item, idx) => (
            <HighlightEditor
              key={item._key}
              item={item}
              index={idx}
              total={form.highlights.length}
              onChange={(val) => updateHighlight(idx, val)}
              onMoveUp={() => moveHighlight(idx, -1)}
              onMoveDown={() => moveHighlight(idx, 1)}
              onRemove={() => removeHighlight(idx)}
            />
          ))}
        </div>
      </div>

      <SeoVideoUpload videoUrl={form.video_url} uploading={uploadingVideo} onUpload={handleVideoUpload} onRemove={removeVideo} />

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="font-semibold text-sm">Preguntas frecuentes</h4>
          <Button data-testid="seo-add-faq-button" variant="outline" size="sm" onClick={addFaq}>
            <Plus size={14} className="mr-1" /> Agregar pregunta
          </Button>
        </div>
        <div>
          <Label>Subtítulo de la sección</Label>
          <Input data-testid="seo-field-faq-title" value={form.faq_title} onChange={(e) => update("faq_title", e.target.value)} />
        </div>
        <div className="space-y-3">
          {form.faq.map((item, idx) => (
            <FAQEditor
              key={item._key}
              item={item}
              index={idx}
              total={form.faq.length}
              onChange={(val) => updateFaq(idx, val)}
              onMoveUp={() => moveFaq(idx, -1)}
              onMoveDown={() => moveFaq(idx, 1)}
              onRemove={() => removeFaq(idx)}
            />
          ))}
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="font-semibold text-sm">Botones</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label>Texto del botón "Cotizar"</Label>
            <Input data-testid="seo-field-quote-button-text" value={form.quote_button_text} onChange={(e) => update("quote_button_text", e.target.value)} />
          </div>
          <div>
            <Label>Texto del botón WhatsApp</Label>
            <Input data-testid="seo-field-whatsapp-button-text" value={form.whatsapp_button_text} onChange={(e) => update("whatsapp_button_text", e.target.value)} />
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <h4 className="font-semibold text-sm">SEO</h4>
        <div>
          <Label>Meta título SEO</Label>
          <Input data-testid="seo-field-seo-title" value={form.seo_title} onChange={(e) => update("seo_title", e.target.value)} />
        </div>
        <div>
          <Label>Meta descripción SEO</Label>
          <Textarea data-testid="seo-field-seo-description" rows={3} value={form.seo_description} onChange={(e) => update("seo_description", e.target.value)} />
        </div>
      </div>

      <Button data-testid="seo-save-button" onClick={save} disabled={saving}>
        {saving ? "Guardando..." : "Guardar cambios"}
      </Button>
    </div>
  );
};
